import React, { useState, useEffect, useRef } from 'react';
import { nfConfirm } from '../components/confirm';
import { createPortal } from 'react-dom';
import { api } from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ProductSearchDropdown from '../components/ProductSearchDropdown';
import Toggle from '../components/Toggle';
import QtyStepper from '../components/QtyStepper';
import PhotoLightbox from '../components/PhotoLightbox';
import { TrashIcon } from '../components/ActionIcons';
import { todayMSKString } from '../utils/timezone';
import { sortBouquets, DAY_SORT_PREFIX } from '../utils/bouquetSort';

const BRAND_GREEN = '#1C3D2E';
const CORAL = '#B0603C';
// Удаление — красный, как во всей админке (кнопки — .btn-danger*, здесь — «×» на фото); терракота — предупреждения (UX-аудит 25.10)
const DANGER = '#CB4D45';
const GRAY_TEXT = '#5E6561';
const BORDER = '#EBEDEC';
const CARD_BG = '#FFFFFF';

const API_BASE = (typeof window !== 'undefined' && window.location)
  ? window.location.origin
  : '';

function resolveImg(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

export default function BouquetsPage({ showToast, embedded = false, archivedMode = false, createTrigger, onCreateHandled }) {
  // tab оставлен для standalone-режима (без embedded). В embedded режиме archived контролируется снаружи.
  const [tab, setTab] = useState('active');
  const [kindFilter, setKindFilter] = useState('all');
  const [bouquets, setBouquets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [disassembling, setDisassembling] = useState(null);
  const [replenishing, setReplenishing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [settings, setSettings] = useState({ bouquet_service_fee_percent: null, service_fee_percent: 11 });
  // Поиск и сортировка — клиентские, как в каталоге стеблей (ProductsPage): список букетов
  // приходит целиком, отдельного запроса к API не нужно.
  const [sortField, setSortField] = useState(null);   // 'name'|'price'|'category'|'assembled'|'hidden'|'assembledOnly'
  const [sortDir, setSortDir] = useState('asc');
  const [searchName, setSearchName] = useState('');
  const [searchInHeader, setSearchInHeader] = useState(false);  // раскрыт ли инпут в шапке колонки

  const effectiveArchive = embedded ? archivedMode : (tab === 'archive');

  useEffect(() => { load(); }, [tab, kindFilter, archivedMode]);
  // Переход активные ↔ архив сбрасывает поиск (как в каталоге стеблей при смене таба)
  useEffect(() => { setSearchName(''); setSearchInHeader(false); }, [tab, archivedMode]);
  useEffect(() => { loadSettings(); }, []);

  // Внешний триггер «+ Добавить букет» из ProductsPage
  useEffect(() => {
    if (createTrigger) {
      setEditing('new');
      onCreateHandled?.();
    }
  }, [createTrigger, onCreateHandled]);

  const loadSettings = async () => {
    try {
      const s = await api.getSettings();
      if (s) setSettings({
        bouquet_service_fee_percent: s.bouquet_service_fee_percent,
        service_fee_percent: s.service_fee_percent,
        // Подключён ли Flowwow: тогда категория площадки и габариты в форме обязательны
        flowwow_enabled: !!s.flowwow_enabled,
      });
    } catch { /* ignore */ }
  };

  const load = async (silent = false) => {
    if (!silent) setIsLoading(true);  // silent: тихий рефетч без флага загрузки (бесшовно)
    try {
      const data = await api.getBouquetsAdmin(effectiveArchive, kindFilter === 'all' ? null : kindFilter);
      setBouquets(data.items || []);
    } catch (e) {
      showToast('Ошибка загрузки букетов', 'error');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!confirmArchive) return;
    try {
      await api.archiveBouquet(confirmArchive.id);
      showToast('Букет в архиве', 'success');
      setConfirmArchive(null);
      load();
    } catch (e) {
      showToast(e.message || 'Не удалось архивировать', 'error');
    }
  };

  const handleRestore = async (id) => {
    try {
      await api.restoreBouquet(id);
      showToast('Букет восстановлен', 'success');
      load();
    } catch (e) {
      showToast(e.message || 'Не удалось восстановить', 'error');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deleteBouquet(confirmDelete.id);
      showToast('Букет удалён', 'success');
      setConfirmDelete(null);
      load();
    } catch (e) {
      showToast(e.message || 'Не удалось удалить', 'error');
    }
  };

  const toggleHidden = async (b) => {
    if (b.is_hidden && !b.category_id) {
      showToast('У букета нет категории — на витрине он не показывается. Выберите категорию в карточке', 'error');
      return;
    }
    // Оптимистично переключаем локально (мгновенно), затем тихий рефетч для синхронизации.
    setBouquets(prev => prev.map(x => x.id === b.id ? { ...x, is_hidden: !x.is_hidden } : x));
    try {
      await api.updateBouquet(b.id, { is_hidden: !b.is_hidden });
      load(true);
    } catch (e) {
      showToast(e.message || 'Ошибка', 'error');
      load(true);  // откат к серверному состоянию
    }
  };

  const toggleAssembledOnly = async (b) => {
    // Оптимистично + тихий рефетч: флаг переключается сразу, матрица доступности
    // обновляется без перезагрузки страницы (assembled_only меняет доступность по датам).
    setBouquets(prev => prev.map(x => x.id === b.id ? { ...x, assembled_only: !x.assembled_only } : x));
    try {
      await api.updateBouquet(b.id, { assembled_only: !b.assembled_only });
      load(true);
    } catch (e) {
      showToast(e.message || 'Ошибка', 'error');
      load(true);  // откат к серверному состоянию
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Клик по дню в шапке: наверх — букеты, которые можно собрать на эту дату; повторный клик снимает
  const selectedDay = sortField && sortField.startsWith(DAY_SORT_PREFIX) ? sortField.slice(DAY_SORT_PREFIX.length) : null;
  const handleDaySort = (iso) => {
    if (selectedDay === iso) { setSortField(null); return; }
    setSortField(DAY_SORT_PREFIX + iso);
    setSortDir('asc');
  };

  const sortedAndFiltered = sortBouquets(bouquets, { searchName, sortField, sortDir });

  const sortIcon = (field) => {
    const inactive = sortField !== field;
    const dir = sortDir === 'asc' ? 'asc' : 'desc';
    return (
      <svg
        width="11" height="11" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ marginLeft: 4, verticalAlign: 'middle', opacity: inactive ? 0.35 : 1 }}
      >
        {inactive ? (
          <>
            <polyline points="7 15 12 20 17 15"/>
            <polyline points="7 9 12 4 17 9"/>
          </>
        ) : dir === 'asc' ? (
          <polyline points="7 14 12 9 17 14"/>
        ) : (
          <polyline points="7 10 12 15 17 10"/>
        )}
      </svg>
    );
  };

  if (isLoading) {
    return embedded ? (
      <div className="loading"><div className="spinner"></div></div>
    ) : (
      <div className="page-container">
        <div className="page-header"><h1>Букеты</h1></div>
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  const Wrapper = embedded ? React.Fragment : ({ children }) => <div className="page-container">{children}</div>;

  // Заголовки дней берём из нефильтрованного списка — при пустом результате поиска шапка остаётся целой
  const dayDates = (bouquets[0]?.daily_availability || []).map(d => d.date);

  const content = (
    <>
      {!embedded && (
        <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h1>Букеты</h1>
          {tab === 'active' && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              + Создать букет
            </button>
          )}
        </div>
      )}

      {/* Активные / Архив — вкладки страницы, как в других разделах (UX-аудит 25.10) */}
      {!embedded && (
        <div className="tabs">
          <button type="button" className={`tab${tab === 'active' ? ' active' : ''}`} onClick={() => setTab('active')}>Активные</button>
          <button type="button" className={`tab${tab === 'archive' ? ' active' : ''}`} onClick={() => setTab('archive')}>Архив</button>
        </div>
      )}

      {bouquets.length === 0 && (
        <div style={{
          textAlign: 'center', padding: 40, marginTop: 16,
          background: CARD_BG, border: `1px solid ${BORDER}`, '--ch': '8px', '--stroke': (BORDER),
          color: GRAY_TEXT,
        }}>
          {effectiveArchive ? 'В архиве пусто' : 'Букетов нет. Создайте первый.'}
        </div>
      )}

      {bouquets.length > 0 && (
        <div className="table-container">
          {/* Раскладка фиксированная, как в «Каталоге»: при авто-раскладке ширины колонок
              пересчитывались под видимые строки, и при вводе в поиск строки «ездили».
              Ширины — в заголовках, «Название» забирает остаток (не уже 150). */}
          <table className="table" style={{
            tableLayout: 'fixed',
            minWidth: 96 + 150 + 110 + 130 + 96 + dayDates.length * 50 + (effectiveArchive ? 200 : 80 + 96),
          }}>
            <thead>
              <tr>
                <th style={{ width: 96 }}>Фото</th>
                <th style={{ userSelect: 'none', position: 'relative' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, visibility: (searchInHeader || searchName) ? 'hidden' : 'visible' }}>
                    <span style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                      Название{sortIcon('name')}
                    </span>
                    <button
                      onClick={() => setSearchInHeader(true)}
                      title="Поиск по названию"
                      style={{
                        width: 28, height: 28, padding: 0, border: 'none',
                        background: 'transparent', cursor: 'pointer',
                        color: BRAND_GREEN, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center',
                        '--ch': '5px', transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(28,61,46,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    </button>
                  </div>
                  {(searchInHeader || searchName) && (
                    <div style={{ position: 'absolute', left: 12, right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                      <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke={BRAND_GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }}
                      >
                        <circle cx="11" cy="11" r="7"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      <input
                        autoFocus
                        type="text"
                        placeholder="Поиск по названию"
                        value={searchName}
                        onChange={e => setSearchName(e.target.value)}
                        onBlur={() => { if (!searchName) setSearchInHeader(false); }}
                        style={{
                          padding: '6px 26px 6px 30px',
                          border: `1px solid ${BRAND_GREEN}`, '--ch': '6px', '--stroke': (BRAND_GREEN),
                          fontSize: 13, fontWeight: 500, width: '100%', boxSizing: 'border-box', outline: 'none',
                          background: '#fff', color: '#1C3D2E',
                          textTransform: 'none', letterSpacing: '0.01em',
                        }}
                      />
                      {searchName && (
                        <button
                          onClick={() => { setSearchName(''); setSearchInHeader(false); }}
                          style={{
                            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                            width: 18, height: 18, border: 'none', background: BRAND_GREEN, borderRadius: '50%',
                            cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          title="Очистить"
                        >×</button>
                      )}
                    </div>
                  )}
                </th>
                <th style={{ width: 130, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('category')}>
                  Категория{sortIcon('category')}
                </th>
                <th style={{ width: 110, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('price')}>
                  Цена{sortIcon('price')}
                </th>
                <th
                  style={{ width: 96, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('assembled')}
                  title="Сортировка по количеству собранных"
                >
                  Собрано{sortIcon('assembled')}
                </th>
                <DayHeaders dates={dayDates} selected={selectedDay} onSelect={handleDaySort} />
                {!effectiveArchive && (
                  <th
                    style={{ textAlign: 'center', width: 80, whiteSpace: 'normal', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('hidden')}
                    title="Сортировка: скрытые сначала / нескрытые сначала"
                  >
                    Виден{sortIcon('hidden')}
                  </th>
                )}
                {!effectiveArchive && (
                  <th
                    style={{ textAlign: 'center', width: 96, whiteSpace: 'normal', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('assembledOnly')}
                    title="Сортировка: сначала с флагом «только собранные» / сначала без"
                  >
                    Только собранные{sortIcon('assembledOnly')}
                  </th>
                )}
                {effectiveArchive && <th style={{ width: 200 }}>Действия</th>}
              </tr>
            </thead>
            <tbody>
              {sortedAndFiltered.length === 0 && (
                <tr>
                  <td colSpan={5 + dayDates.length + (effectiveArchive ? 1 : 2)} style={{ textAlign: 'center', padding: '40px 20px', color: GRAY_TEXT }}>
                    Ничего не найдено
                  </td>
                </tr>
              )}
              {sortedAndFiltered.map(b => (
                <BouquetRow
                  key={b.id}
                  b={b}
                  inArchive={effectiveArchive}
                  onEdit={() => setEditing(b)}
                  onDelete={() => setConfirmDelete(b)}
                  onRestore={() => handleRestore(b.id)}
                  onToggleHidden={() => toggleHidden(b)}
                  onToggleAssembledOnly={() => toggleAssembledOnly(b)}
                  onReplenish={() => setReplenishing(b)}
                  onDisassemble={() => setDisassembling(b)}
                  selectedDay={selectedDay}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <BouquetFormModal
          bouquet={editing === 'new' ? null : editing}
          settings={settings}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          showToast={showToast}
          onAssemble={(b) => { setEditing(null); setReplenishing(b); }}
          onDisassemble={(b) => { setEditing(null); setDisassembling(b); }}
          onArchive={(b) => { setEditing(null); setConfirmArchive(b); }}
          onRestore={(b) => { setEditing(null); handleRestore(b.id); }}
          onDelete={(b) => { setEditing(null); setConfirmDelete(b); }}
        />
      )}

      {disassembling && (
        <BouquetDisassembleModal
          bouquet={disassembling}
          onClose={() => setDisassembling(null)}
          onDone={() => { setDisassembling(null); load(); }}
          showToast={showToast}
        />
      )}

      {replenishing && (
        <BouquetReplenishModal
          bouquet={replenishing}
          onClose={() => setReplenishing(null)}
          onDone={() => { setReplenishing(null); load(); }}
          showToast={showToast}
        />
      )}

      {confirmArchive && (
        <ConfirmDialog
          title="В архив?"
          message={`Букет «${confirmArchive.name}» перейдёт в архив и исчезнет с витрины.`}
          confirmText="В архив"
          cancelText="Отмена"
          onConfirm={handleArchive}
          onCancel={() => setConfirmArchive(null)}
          variant="danger"
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Удалить навсегда?"
          message={`Букет «${confirmDelete.name}» будет удалён безвозвратно. История завершённых заказов сохранится (имя и цена в snapshot OrderItem).`}
          confirmText="Удалить"
          cancelText="Отмена"
          onConfirm={handleDelete}
          variant="danger"
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </>
  );

  return <Wrapper>{content}</Wrapper>;
}


function BouquetRow({ b, inArchive, onEdit, onDelete, onRestore, onToggleHidden, onToggleAssembledOnly, onReplenish, onDisassemble, selectedDay }) {
  const mainImage = b.images?.[0]?.image_small || b.images?.[0]?.image_url;
  const phys = b.physical_quantity || 0;
  const pending = b.pending_assembly_qty || 0;
  // Выбран день в шапке, а на него этот букет не собрать — строка бледнеет (она уже внизу списка)
  const offDay = selectedDay && !((b.daily_availability || []).find(d => d.date === selectedDay)?.capacity > 0);
  const dimStyle = b.is_hidden || offDay ? { opacity: 0.55 } : {};

  const handleRowClick = (e) => {
    if (e.target.closest('input,button,[data-no-row-click]')) return;
    onEdit?.();
  };

  return (
    <tr onClick={handleRowClick} style={{ cursor: 'pointer' }}>
      <td style={dimStyle}>
        {mainImage ? (
          <img src={resolveImg(mainImage)} alt={b.name}
               style={{ width: 60, height: 60, objectFit: 'cover', '--ch': '5px'}} />
        ) : (
          <div style={{
            width: 60, height: 60, background: '#F4F1EC', '--ch': '5px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28,
          }}>💐</div>
        )}
      </td>
      {/* Длинное название без пробелов («TEST_bq_unc_mix_739896») переносим внутри слова —
          иначе оно не сжимается и вылезает на колонку цены */}
      <td style={{ ...dimStyle, fontWeight: 500, overflowWrap: 'anywhere', paddingRight: 12 }}>
        {b.name}
        {b.has_active_orders && (
          <div style={{ fontSize: 11, color: CORAL, marginTop: 2 }}>есть активные заказы</div>
        )}
      </td>
      <td style={{ ...dimStyle, color: '#5E6561' }} title={b.category ? undefined : 'Без категории на витрине не показывается'}>
        {b.category?.name || '—'}
      </td>
      <td style={dimStyle}>
        <PricePopover bouquet={b}>
          <span style={{ fontWeight: 700, fontSize: 15, color: BRAND_GREEN, cursor: 'help' }}>
            {b.final_price?.toLocaleString('ru-RU')} ₽
          </span>
        </PricePopover>
      </td>
      <td style={{ ...dimStyle, textAlign: 'center' }} data-no-row-click onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {/* −/+ — общий .btn-icon, размер 22 под узкую колонку; «−» не терракотовый (UX-аудит 25.10) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
            {!inArchive && (b.physical_quantity || 0) > 0 && onDisassemble && (
              <button
                type="button"
                className="btn-icon"
                onClick={onDisassemble}
                title="Разобрать собранные букеты"
                style={{ width: 22, height: 22, fontSize: 15, fontWeight: 700, lineHeight: 1 }}
              >−</button>
            )}
            {phys > 0 ? (
              <span style={{ fontWeight: 700, fontSize: 16, color: BRAND_GREEN }}>{phys}</span>
            ) : (
              <span style={{ color: GRAY_TEXT }}>—</span>
            )}
            {!inArchive && (
              <button
                type="button"
                className="btn-icon"
                onClick={onReplenish}
                title="Пополнить готовыми / задание флористу"
                style={{ width: 22, height: 22, fontSize: 15, fontWeight: 700, lineHeight: 1 }}
              >+</button>
            )}
          </div>
          {pending > 0 && (
            <span style={{ fontSize: 11, color: '#7E5D00', fontWeight: 600 }}>в работе: {pending}</span>
          )}
          {(b.expired_lots_count || 0) > 0 && (
            <span style={{ fontSize: 11, color: CORAL, fontWeight: 700 }}>истекло: {b.expired_lots_count}</span>
          )}
        </div>
      </td>
      <DayCells daily={b.daily_availability} selected={selectedDay} />
      {!inArchive && (
        <td style={{ textAlign: 'center', width: 80, ...dimStyle }} data-no-row-click onClick={e => e.stopPropagation()}>
          <Toggle
            checked={!b.is_hidden}
            onChange={onToggleHidden}
          />
        </td>
      )}
      {!inArchive && (
        <td style={{ textAlign: 'center', width: 80, ...dimStyle }} data-no-row-click onClick={e => e.stopPropagation()}>
          <Toggle
            checked={!!b.assembled_only}
            onChange={onToggleAssembledOnly}
          />
        </td>
      )}
      {inArchive && (
        <td data-no-row-click onClick={e => e.stopPropagation()}>
          {/* Действия в строке: «Восстановить» — второстепенная sm, удаление — красная корзина (UX-аудит 25.10) */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {phys === 0 && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={onRestore}>Восстановить</button>
            )}
            <button type="button" className="btn-icon is-danger" onClick={onDelete} title="Удалить навсегда" aria-label="Удалить навсегда">
              <TrashIcon />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}


const MONTH_NAMES = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

// Заголовки дней: 14 узких <th> с числом и (для первого дня каждого месяца) подписью месяца.
function DayHeaders({ dates, selected, onSelect }) {
  if (!dates || dates.length === 0) return null;
  const todayIso = dates[0];
  let prevMonth = null;

  return (
    <>
      {dates.map(iso => {
        const dt = new Date(iso + 'T00:00:00');
        const day = dt.getDate();
        const month = dt.getMonth();
        const isToday = iso === todayIso;
        const isSel = iso === selected;
        const showMonth = prevMonth !== month || isToday;
        prevMonth = month;
        return (
          <th
            key={iso}
            onClick={() => onSelect?.(iso)}
            title={isSel ? 'Снять выбор дня' : 'Показать наверху букеты, которые можно собрать на эту дату'}
            style={{
              width: 50,
              minWidth: 50,
              padding: '8px 2px',
              textAlign: 'center',
              fontWeight: isToday || isSel ? 700 : 500,
              fontSize: 13,
              color: isSel ? '#fff' : isToday ? '#1C3D2E' : '#5E6561',
              background: isSel ? '#1C3D2E' : isToday ? '#EFFAF4' : undefined,
              borderRight: '1px solid #EBEDEC',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <div style={{ fontSize: 16, lineHeight: 1.1 }}>{day}</div>
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>
              {showMonth ? MONTH_NAMES[month] : ' '}
            </div>
          </th>
        );
      })}
    </>
  );
}

// 14 ячеек тела для одного букета — только цифра capacity с цветным фоном.
function DayCells({ daily, selected }) {
  if (!daily || daily.length === 0) return null;
  const todayIso = daily[0]?.date;
  return (
    <>
      {daily.map(d => {
        const cap = d.capacity || 0;
        const isToday = d.date === todayIso;
        const bg = cap > 0 ? '#DCF4E0' : '#FFE7DD';
        const txt = cap > 0 ? '#1F763B' : '#B0603C';
        return (
          <td
            key={d.date}
            title={cap > 0 ? `${cap} шт.` : 'нет в наличии'}
            style={{
              padding: 4,
              textAlign: 'center',
              borderRight: '1px solid #EBEDEC',
              background: d.date === selected ? '#DFF2E8' : isToday ? '#EFFAF4' : undefined,
            }}
          >
            <div style={{
              background: bg,
              color: txt,
              '--ch': '6px',
              fontSize: 18,
              fontWeight: 700,
              padding: '12px 0',
              minWidth: 40,
            }}>
              {cap > 0 ? cap : '—'}
            </div>
          </td>
        );
      })}
    </>
  );
}


// Popover при наведении на цену: показывает состав букета и его компонентные цены.
// Использует Portal для размещения поверх таблицы.
function PricePopover({ bouquet, children }) {
  const [state, setState] = useState({ visible: false, x: 0, y: 0 });
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  const show = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setState({ visible: true, x: rect.left + rect.width / 2, y: rect.bottom + 8 });
    }, 120);
  };
  const hide = () => {
    clearTimeout(timerRef.current);
    setState(s => ({ ...s, visible: false }));
  };

  const components = bouquet.components || [];
  // bouquet.components_total — сумма line_total после скейла (= bouquet.price).
  // Fallback: вычисляем как сумма line_total компонентов, иначе из product_price.
  const componentsTotal = bouquet.components_total
    ?? components.reduce((s, c) => s + (c.line_total != null ? c.line_total : (c.product_price || 0) * c.quantity), 0);
  const serviceFee = bouquet.service_fee ?? ((bouquet.final_price || 0) - (bouquet.price || 0));
  const serviceFeePct = bouquet.service_fee_percent;

  return (
    <>
      <span ref={wrapRef} onMouseEnter={show} onMouseLeave={hide}
        style={{ display: 'inline-block' }}>
        {children}
      </span>
      {state.visible && createPortal(
        <div
          style={{
            position: 'fixed',
            left: state.x, top: state.y,
            transform: 'translateX(-50%)',
            background: '#fff',
            border: '1px solid #EBEDEC',
            '--ch': '8px', '--stroke': '#EBEDEC',
            padding: 14,
            minWidth: 280,
            maxWidth: 420,
            boxShadow: '0 6px 24px rgba(28,61,46,0.15)',
            zIndex: 10000,
            pointerEvents: 'none',
            fontSize: 14,
          }}
        >
          <div style={{ fontWeight: 600, color: BRAND_GREEN, marginBottom: 8 }}>
            Состав «{bouquet.name}»
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {components.map(c => {
                const lineTotal = c.line_total != null ? c.line_total : (c.product_price || 0) * c.quantity;
                return (
                  <tr key={c.id}>
                    <td style={{ padding: '4px 8px 4px 0', color: '#1C3D2E' }}>{c.product_name}</td>
                    <td style={{ padding: '4px 0', textAlign: 'right', color: '#5E6561', whiteSpace: 'nowrap' }}>
                      × {c.quantity}
                    </td>
                    <td style={{ padding: '4px 0 4px 8px', textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {lineTotal.toLocaleString('ru-RU')} ₽
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ borderTop: '1px solid #EBEDEC', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#5E6561' }}>Стоимость цветов:</span>
            <span style={{ fontWeight: 500 }}>{componentsTotal.toLocaleString('ru-RU')} ₽</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#5E6561' }}>Сервисный сбор{serviceFeePct ? ` (${Math.round(serviceFeePct)}%)` : ''}:</span>
            <span style={{ fontWeight: 500 }}>{serviceFee.toLocaleString('ru-RU')} ₽</span>
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            marginTop: 4, paddingTop: 6, borderTop: '1px solid #EBEDEC',
            fontWeight: 600, color: BRAND_GREEN, fontSize: 15,
          }}>
            <span>Итого:</span>
            <span>{(bouquet.final_price || 0).toLocaleString('ru-RU')} ₽</span>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}


// Брендовый кастомный селект (вместо нативного <select>): белый фон, зелёный акцент,
// чек у выбранного пункта, закрытие по клику вне.
function StyledSelect({ value, onChange, options, placeholder = 'Выберите', disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const selected = options.find(o => String(o.value) === String(value));
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          // Размеры как у .form-input (12px 16px, 15px) — рядом с текстовыми полями без разнобоя
          padding: '12px 16px', background: disabled ? '#F4F4EF' : '#fff',
          border: `1px solid ${open ? BRAND_GREEN : BORDER}`, '--ch': '6px', '--stroke': (open ? BRAND_GREEN : BORDER), fontSize: 15,
          color: selected ? '#1C3D2E' : GRAY_TEXT, cursor: disabled ? 'default' : 'pointer',
          fontFamily: 'inherit', textAlign: 'left', transition: 'border-color .15s, --stroke .15s', boxSizing: 'border-box',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : placeholder}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GRAY_TEXT} strokeWidth="2.5"
          style={{ flexShrink: 0, marginLeft: 8, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: `1px solid ${BORDER}`, '--ch': '6px', '--stroke': (BORDER),
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden auto', maxHeight: 260,
        }}>
          {options.map(o => {
            const active = String(o.value) === String(value);
            return (
              <div
                key={String(o.value)}
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  padding: '10px 16px', fontSize: 15, cursor: 'pointer',
                  background: active ? '#EFFAF4' : '#fff', color: active ? BRAND_GREEN : '#1C3D2E',
                  fontWeight: active ? 600 : 400, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#F6F6F3'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#fff'; }}
              >
                <span>{o.label}</span>
                {active && <span style={{ color: BRAND_GREEN, fontSize: 13 }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Сколько стеблей не хватает на 1 экземпляр букета. Из истёкших собирать нельзя → к сборке 0.
function componentShortage(c) {
  if (c.product_available == null) return 0;
  const need = parseInt(c.quantity) || 0;
  const avail = c.product_is_expired ? 0 : (c.product_available || 0);
  return Math.max(0, need - avail);
}

function BouquetFormModal({ bouquet, settings, onClose, onSaved, showToast, onAssemble, onDisassemble, onArchive, onRestore, onDelete }) {
  const isNew = !bouquet;
  const [name, setName] = useState(bouquet?.name || '');
  const [description, setDescription] = useState(bouquet?.description || '');
  const [categoryId, setCategoryId] = useState(bouquet?.category_id || '');
  const [bouquetCategories, setBouquetCategories] = useState([]);
  useEffect(() => {
    api.getCategories('bouquets').then(setBouquetCategories).catch(() => setBouquetCategories([]));
  }, []);
  const [isHidden, setIsHidden] = useState(bouquet?.is_hidden || false);
  // Габариты и категория для Flowwow; при подключённой интеграции обязательны
  // (предзаполняются автоматом при подключении, здесь продавец подтверждает или меняет)
  const flowwowEnabled = !!settings?.flowwow_enabled;
  const [widthCm, setWidthCm] = useState(bouquet?.width_cm != null ? String(bouquet.width_cm) : '');
  const [heightCm, setHeightCm] = useState(bouquet?.height_cm != null ? String(bouquet.height_cm) : '');
  const [fwCategory, setFwCategory] = useState(bouquet?.flowwow_sub_category_id != null ? String(bouquet.flowwow_sub_category_id) : '');
  // «Название для Flowwow» — необязательно; пусто — на площадку уходит обычное название
  const [fwName, setFwName] = useState(bouquet?.flowwow_name || '');
  const [components, setComponents] = useState(
    bouquet?.components?.map(c => ({
      product_id: c.product_id,
      product_name: c.product_name,
      product_price: c.product_price,
      quantity: c.quantity,
      product_available: c.product_available,
      product_expiry_date: c.product_expiry_date,
      product_is_expired: c.product_is_expired,
    })) || []
  );
  const [isCustomPrice, setIsCustomPrice] = useState(bouquet?.is_custom_price ?? false);
  const [customPrice, setCustomPrice] = useState(bouquet?.price ?? null);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [images, setImages] = useState(bouquet?.images || []);
  // pendingFiles — выбранные фото при создании (до сохранения). Загрузятся после POST /bouquets.
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [lightbox, setLightbox] = useState(null); // { images: [{src}], index }

  const feePct = settings.bouquet_service_fee_percent ?? settings.service_fee_percent ?? 11;
  const componentsTotal = components.reduce(
    (s, c) => s + (c.product_price || 0) * (c.quantity || 0), 0
  );
  // Компоненты, которых не хватает даже на 1 экземпляр — букет не соберётся.
  const shortages = components
    .map(c => ({ name: c.product_name, short: componentShortage(c) }))
    .filter(s => s.short > 0);
  const effectivePrice = isCustomPrice ? (customPrice ?? componentsTotal) : componentsTotal;
  const serviceFeeAmount = Math.round(effectivePrice * feePct / 100);
  const finalPrice = effectivePrice + serviceFeeAmount;

  const addComponent = (product) => {
    setComponents(prev => [
      ...prev,
      {
        product_id: product.id, product_name: product.name, product_price: product.price,
        quantity: 1,
        product_available: product.quantity ?? product.physical_quantity ?? null,  // остаток склада для подсказки нехватки
      }
    ]);
    setShowProductSearch(false);
  };

  const updateComponent = (idx, patch) => {
    setComponents(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  };

  const removeComponent = (idx) => {
    setComponents(prev => prev.filter((_, i) => i !== idx));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!bouquet?.id) {
      // Новый букет — копим файлы pending, загрузим после создания
      setPendingFiles(prev => [...prev, ...files]);
      e.target.value = '';
      return;
    }
    setImageBusy(true);
    try {
      for (const file of files) {
        const img = await api.uploadBouquetImage(bouquet.id, file);
        setImages(prev => [...prev, img]);
      }
      showToast('Фото загружено', 'success');
    } catch (err) {
      showToast(err.message || 'Ошибка загрузки', 'error');
    } finally {
      setImageBusy(false);
      e.target.value = '';
    }
  };

  const removePending = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleImageDelete = async (imageId) => {
    if (!bouquet?.id) return;
    try {
      await api.deleteBouquetImage(bouquet.id, imageId);
      setImages(prev => prev.filter(i => i.id !== imageId));
    } catch (err) {
      showToast(err.message || 'Ошибка удаления', 'error');
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { showToast('Введите название', 'error'); return; }
    if (components.length === 0) { showToast('Добавьте хотя бы один компонент', 'error'); return; }
    if (flowwowEnabled) {
      if (!fwCategory) { showToast('Выберите категорию букета на Flowwow', 'error'); return; }
      if (!(parseInt(widthCm, 10) > 0) || !(parseInt(heightCm, 10) > 0)) { showToast('Укажите ширину и высоту букета, см — Flowwow требует габариты', 'error'); return; }
      if (!isNew && bouquet?.flowwow_sub_category_id != null && String(bouquet.flowwow_sub_category_id) !== fwCategory
          && !(await nfConfirm('Категория на Flowwow изменится: площадка не даёт менять её у карточки, поэтому будет создана новая карточка, а старая уйдёт в архив (история и отзывы у старой останутся). Продолжить?', { confirmText: 'Продолжить' }))) {
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description || null,
        is_custom_price: isCustomPrice,
        price: isCustomPrice ? (customPrice ?? componentsTotal) : null,
        is_hidden: isHidden,
        category_id: categoryId ? parseInt(categoryId) : null,
        width_cm: widthCm !== '' ? parseInt(widthCm, 10) || null : null,
        height_cm: heightCm !== '' ? parseInt(heightCm, 10) || null : null,
        flowwow_sub_category_id: fwCategory ? parseInt(fwCategory, 10) : null,
        ...(flowwowEnabled ? { flowwow_name: fwName.trim() || null } : {}),
        components: components.map((c, i) => ({
          product_id: c.product_id, quantity: c.quantity, sort_order: i,
        })),
      };
      if (isNew) {
        // Создаём всегда как recipe — физика добавляется отдельно через «Собрать впрок»
        payload.kind = 'recipe';
        const created = await api.createBouquet(payload);
        // Загружаем pending-фото после создания
        if (pendingFiles.length > 0 && created?.id) {
          for (const file of pendingFiles) {
            try { await api.uploadBouquetImage(created.id, file); } catch (e) {
              showToast(`Не удалось загрузить «${file.name}»: ${e.message}`, 'error');
            }
          }
        }
        showToast('Букет создан', 'success');
      } else {
        // Если есть собранная партия — состав менять нельзя
        if ((bouquet.physical_quantity || 0) > 0 || bouquet.has_active_orders) {
          delete payload.components;
        }
        await api.updateBouquet(bouquet.id, payload);
        showToast('Букет обновлён', 'success');
      }
      onSaved();
    } catch (e) {
      showToast(e.message || 'Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  const componentsLocked = !isNew && ((bouquet.physical_quantity || 0) > 0 || bouquet.has_active_orders);
  // Под формой — только сборка/разборка/восстановление; «В архив» и «Удалить навсегда» — в подвале слева
  const hasBodyActions = !isNew && !!(bouquet.is_active
    ? (((bouquet.physical_quantity || 0) === 0 && onAssemble) || ((bouquet.physical_quantity || 0) > 0 && onDisassemble))
    : ((bouquet.physical_quantity || 0) === 0 && onRestore));

  return (
    <Modal
      onClose={onClose}
      confirmOverlayClose
      title={isNew ? 'Новый букет' : `Редактирование: ${bouquet.name}`}
      size="large"
      footer={
        <>
          {/* Подвал: слева красная «В архив» / «Удалить навсегда», справа [Отмена] [Главная] (UX-аудит 25.10) */}
          {!isNew && bouquet.is_active && onArchive && (
            <button type="button" className="btn btn-danger-outline" onClick={() => onArchive(bouquet)} style={{ marginRight: 'auto' }}>
              В архив
            </button>
          )}
          {!isNew && !bouquet.is_active && onDelete && (
            <button type="button" className="btn btn-danger-outline" onClick={() => onDelete(bouquet)} style={{ marginRight: 'auto' }}>
              Удалить навсегда
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохраняем...' : (isNew ? 'Создать' : 'Сохранить')}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Видимость — плашка сверху */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', background: '#F4F4EF', '--ch': '8px', '--stroke': '#D8DDDA',
          border: '1px solid #D8DDDA',
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#1C3D2E' }}>
              {isHidden ? 'Скрыт из каталога' : 'Виден в каталоге'}
            </div>
            <div style={{ fontSize: 12, color: GRAY_TEXT, marginTop: 2 }}>
              {!categoryId
                ? 'Без категории на витрине не показывается — выберите категорию'
                : isHidden ? 'Букет не показывается клиентам' : 'Букет доступен на витрине'}
            </div>
          </div>
          <Toggle checked={!isHidden} onChange={(v) => setIsHidden(!v)} />
        </div>

        {/* Фото */}
        <div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Уже загруженные (для существующего букета) */}
            {images.map((img, imgIdx) => (
              <div key={img.id} style={{ position: 'relative', width: 100, height: 100 }}>
                <img src={resolveImg(img.image_small || img.image_url)} alt=""
                     onClick={() => setLightbox({
                       images: images.map(im => ({ src: resolveImg(im.image_large || im.image_url || im.image_small) })),
                       index: imgIdx,
                     })}
                     style={{ width: 100, height: 100, objectFit: 'cover', '--ch': '8px', '--stroke': (BORDER), border: `1px solid ${BORDER}`, cursor: 'zoom-in' }} />
                {/* «×» на фото — удаление, красным, не терракотой (UX-аудит 25.10) */}
                <button type="button" onClick={() => handleImageDelete(img.id)} title="Удалить фото" style={{
                  position: 'absolute', top: 6, right: 6,
                  width: 22, height: 22, borderRadius: '50%', border: 'none',
                  background: DANGER, color: '#fff', cursor: 'pointer',
                  fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}>×</button>
              </div>
            ))}
            {/* Pending (выбранные при создании, ещё не загруженные на сервер) */}
            {pendingFiles.map((file, idx) => (
              <div key={`p-${idx}`} style={{ position: 'relative', width: 100, height: 100 }}>
                <img src={URL.createObjectURL(file)} alt=""
                     style={{ width: 100, height: 100, objectFit: 'cover', '--ch': '8px', '--stroke': (BORDER), border: `1px solid ${BORDER}` }} />
                <button type="button" onClick={() => removePending(idx)} title="Убрать фото" style={{
                  position: 'absolute', top: 6, right: 6,
                  width: 22, height: 22, borderRadius: '50%', border: 'none',
                  background: DANGER, color: '#fff', cursor: 'pointer',
                  fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}>×</button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageBusy}
              title="Загрузить фото"
              style={{
                width: 100, height: 100, border: '2px dashed #D8DDDA', '--ch': '8px', '--stroke': '#D8DDDA', '--bw': '2px',
                background: '#fff', cursor: 'pointer',
                fontSize: 32, color: '#1C3D2E',
                display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
              }}
            >+</button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple
                   onChange={handleImageUpload} style={{ display: 'none' }} />
          </div>
        </div>

        {/* Название + Категория в одну строку */}
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="form-group" style={{ flex: 1, margin: 0 }}>
            <label className="form-label">Название <span style={{ color: CORAL }}>*</span></label>
            <input
              type="text"
              className="form-input"
              placeholder="Название букета"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ flex: 1, margin: 0 }}>
            <label className="form-label">Категория</label>
            <StyledSelect
              value={categoryId}
              onChange={(v) => setCategoryId(v)}
              placeholder="Без категории"
              options={[
                { value: '', label: 'Без категории' },
                ...bouquetCategories.map(c => ({ value: c.id, label: c.name })),
              ]}
            />
            {bouquetCategories.length === 0 && (
              <small style={{ color: GRAY_TEXT, fontSize: 11 }}>
                Категорий нет. Создайте в «Каталог → Категории».
              </small>
            )}
          </div>
        </div>

        {/* Flowwow: категория площадки и габариты. Без интеграции — только габариты, необязательно */}
        <div>
          {/* Название и категория на Flowwow — строкой под нашими «Название» и «Категория» */}
          {flowwowEnabled && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
              <div className="form-group" style={{ flex: 1, margin: 0 }}>
                <label className="form-label">Название на Flowwow</label>
                <input
                  type="text"
                  className="form-input"
                  maxLength={255}
                  value={fwName}
                  onChange={e => setFwName(e.target.value)}
                  placeholder={`Пусто — уйдёт «${name.trim() || 'название букета'}»`}
                />
                <small style={{ display: 'block', marginTop: 4, color: GRAY_TEXT, fontSize: 11 }}>
                  Что за товар и из каких цветов, номер в конце: «Букет из калл и роз №4». Без капса, цен и эмодзи.
                  Смена названия может отправить карточку на повторную модерацию.
                </small>
              </div>
              <div className="form-group" style={{ flex: 1, margin: 0 }}>
                <label className="form-label">Категория на Flowwow <span style={{ color: '#CB4D45' }}>*</span></label>
                <StyledSelect
                  value={fwCategory}
                  onChange={(v) => setFwCategory(String(v))}
                  placeholder="— выберите —"
                  options={[
                    { value: '1', label: 'Монобукеты (один вид цветов)' },
                    { value: '2', label: 'Авторские букеты (несколько видов)' },
                    { value: '3', label: 'Цветы в коробке' },
                    { value: '4', label: 'Цветы в корзине' },
                    { value: '7', label: 'Композиции из цветов (банка, ваза, кашпо)' },
                  ]}
                />
                <small style={{ display: 'block', marginTop: 4, color: GRAY_TEXT, fontSize: 11 }}>
                  Раздел каталога Flowwow, где покупатель увидит букет. Подставлено по составу автоматически — проверьте.
                  Смена категории у опубликованного букета создаёт на площадке новую карточку.
                </small>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1, margin: 0 }}>
              <label className="form-label">Ширина, см{flowwowEnabled && <span style={{ color: '#CB4D45' }}> *</span>}</label>
              <input
                type="number"
                className="form-input"
                placeholder="—"
                min="1"
                required={flowwowEnabled}
                value={widthCm}
                onChange={e => setWidthCm(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ flex: 1, margin: 0 }}>
              <label className="form-label">Высота, см{flowwowEnabled && <span style={{ color: '#CB4D45' }}> *</span>}</label>
              <input
                type="number"
                className="form-input"
                placeholder="—"
                min="1"
                required={flowwowEnabled}
                value={heightCm}
                onChange={e => setHeightCm(e.target.value)}
              />
            </div>
          </div>
          <small style={{ display: 'block', marginTop: 4, color: GRAY_TEXT, fontSize: 11 }}>
            {flowwowEnabled
              ? 'Габариты обязательны для карточки на Flowwow (покупатель видит размер букета)'
              : 'Для маркетплейсов (Flowwow); понадобится при подключении интеграции'}
          </small>
        </div>

        {/* Состав и цена — одна карточка: строки состава на белом, итог по цене светло-зелёной полосой */}
        <div>
          <Label>Состав букета · на 1 экземпляр{componentsLocked && <span style={{ color: GRAY_TEXT, fontWeight: 400, fontSize: 11 }}> — заблокирован (есть собранная партия или активные заказы)</span>}</Label>
          {/* Рамка и срез одного цвета (#D8DDDA), без overflow: hidden — он съедает контур среза */}
          <div style={{ border: '1px solid #D8DDDA', '--ch': '8px', '--stroke': '#D8DDDA', background: CARD_BG }}>
            {components.length === 0 && (
              <div style={{ padding: '14px 16px', color: GRAY_TEXT, fontSize: 13 }}>Цветов в составе пока нет</div>
            )}
            {components.map((c, i) => {
              const short = componentShortage(c);
              const meta = [`${(c.product_price || 0).toLocaleString('ru-RU')} ₽/шт`];
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                  borderTop: i > 0 ? `1px solid ${BORDER}` : 'none',
                  background: short > 0 ? '#FFF4F0' : CARD_BG,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 500, color: BRAND_GREEN, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.product_name}
                    </div>
                    <div style={{ fontSize: 12, color: GRAY_TEXT, marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '0 10px' }}>
                      <span>{meta[0]}</span>
                      {c.product_available != null && (
                        <span style={{ color: short > 0 ? CORAL : '#4A8C6E', fontWeight: 600 }}>
                          в наличии {c.product_available} шт
                          {short > 0 && (c.product_is_expired ? ' · истёк, к сборке 0' : ` · не хватает ${short}`)}
                        </span>
                      )}
                      {c.product_expiry_date && (
                        <span style={{ color: c.product_is_expired ? CORAL : GRAY_TEXT }}>
                          {c.product_is_expired ? 'истёк' : 'годен до'} {new Date(c.product_expiry_date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <QtyStepper value={c.quantity} min={1} disabled={componentsLocked} onChange={q => updateComponent(i, { quantity: q })} />
                  <span style={{ fontSize: 14, color: BRAND_GREEN, minWidth: 76, textAlign: 'right', fontWeight: 600, flexShrink: 0 }}>
                    {((c.product_price || 0) * c.quantity).toLocaleString('ru-RU')} ₽
                  </span>
                  {!componentsLocked && (
                    <button type="button" onClick={() => removeComponent(i)} title="Убрать из состава" style={{
                      background: 'none', border: 'none', color: '#909894',
                      cursor: 'pointer', fontSize: 18, padding: '0 2px', lineHeight: 1, flexShrink: 0,
                    }}>×</button>
                  )}
                </div>
              );
            })}

            {/* Добавить цветок — последней строкой карточки */}
            {!componentsLocked && (
              <div style={{ borderTop: components.length > 0 ? `1px solid ${BORDER}` : 'none', padding: showProductSearch ? 10 : '12px 16px' }}>
                {showProductSearch ? (
                  <ProductSearchDropdown
                    onSelect={addComponent}
                    onClose={() => setShowProductSearch(false)}
                    excludeIds={components.map(c => c.product_id)}
                  />
                ) : (
                  // «+ Добавить цветок» — ссылка-действие, как «+ Добавить стебли» в заказе (UX-аудит 25.10)
                  <button type="button" className="btn-link" onClick={() => setShowProductSearch(true)}>+ Добавить цветок</button>
                )}
              </div>
            )}

            {shortages.length > 0 && (
              <div style={{
                padding: '8px 16px', borderTop: `1px solid ${BORDER}`,
                background: '#FFF4F0', color: CORAL, fontSize: 12, fontWeight: 600,
              }}>
                {/* Задание «в работе» уже держит свои стебли: нехватка — для ещё одного букета, состав менять можно (UX 25.10) */}
                {bouquet?.pending_assembly_qty > 0
                  ? `Ещё один букет сверх задания в работе не собрать — не хватает: ${shortages.map(s => `${s.name} (${s.short})`).join(', ')}. Состав менять можно: бронь задания пересчитается`
                  : `Собрать нельзя — не хватает: ${shortages.map(s => `${s.name} (${s.short})`).join(', ')}`}
              </div>
            )}

            <div style={{ borderTop: `1px solid ${BORDER}`, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 13 }}>
              <Toggle
                checked={isCustomPrice}
                onChange={(on) => {
                  setIsCustomPrice(on);
                  if (on && customPrice == null) setCustomPrice(componentsTotal);
                }}
                label="Своя цена"
              />
              {isCustomPrice ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: GRAY_TEXT }}>
                  цветы за
                  <input
                    type="number" placeholder={String(componentsTotal)}
                    value={customPrice ?? ''}
                    onChange={e => setCustomPrice(e.target.value === '' ? null : Math.max(0, parseInt(e.target.value) || 0))}
                    style={{ width: 110, padding: '6px 10px', border: `1px solid ${BORDER}`, '--ch': '5px', '--stroke': BORDER, fontSize: 14, color: BRAND_GREEN }}
                  />
                  ₽, сбор сверху
                </span>
              ) : (
                <span style={{ color: GRAY_TEXT }}>авто — сумма цветов, меняется вместе с ценой стеблей</span>
              )}
            </div>
            {/* Цена: строки расчёта на белом, светло-зелёная полоса итога — последней */}
            <div style={{ borderTop: `1px solid ${BORDER}`, background: '#fff', padding: '10px 16px 8px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: GRAY_TEXT }}>
                <span>Стоимость цветов{isCustomPrice ? ' по составу' : ''}</span>
                <span style={isCustomPrice
                  ? { color: GRAY_TEXT, textDecoration: 'line-through' }
                  : { color: BRAND_GREEN, fontWeight: 500 }}>{componentsTotal.toLocaleString('ru-RU')} ₽</span>
              </div>
              {isCustomPrice && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: GRAY_TEXT }}>
                  <span>Своя цена</span>
                  <span style={{ color: BRAND_GREEN, fontWeight: 500 }}>{effectivePrice.toLocaleString('ru-RU')} ₽</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', color: GRAY_TEXT }}>
                <span>Сервисный сбор букетов · {Math.round(feePct)} %</span>
                <span style={{ color: BRAND_GREEN, fontWeight: 500 }}>{serviceFeeAmount.toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>
            <div style={{ background: '#DFF2E8', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: BRAND_GREEN }}>Цена для клиента</span>
              <span style={{ fontWeight: 800, fontSize: 20, color: BRAND_GREEN }}>{finalPrice.toLocaleString('ru-RU')} ₽</span>
            </div>
          </div>
        </div>

        {/* Описание */}
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Описание</label>
          <textarea
            className="form-input"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Описание букета"
            rows={3}
            style={{ resize: 'vertical' }}
          />
          {flowwowEnabled && (
            <small style={{ display: 'block', marginTop: 4, color: GRAY_TEXT, fontSize: 11 }}>
              На витрине не показывается — уходит на Flowwow.
            </small>
          )}
        </div>

        {/* Действия для существующего букета; «В архив» / «Удалить навсегда» — в подвале */}
        {hasBodyActions && (
          <div style={{
            display: 'flex', gap: 8, flexWrap: 'wrap',
            paddingTop: 12, borderTop: `1px solid ${BORDER}`,
          }}>
            {bouquet.is_active ? (
              <>
                {(bouquet.physical_quantity || 0) === 0 && onAssemble && (
                  <button type="button" className="btn btn-secondary btn-md" onClick={() => onAssemble(bouquet)}>
                    Собрать впрок
                  </button>
                )}
                {(bouquet.physical_quantity || 0) > 0 && onDisassemble && (
                  <button type="button" className="btn btn-secondary btn-md" onClick={() => onDisassemble(bouquet)}>
                    Разобрать ({bouquet.physical_quantity})
                  </button>
                )}
              </>
            ) : (
              (bouquet.physical_quantity || 0) === 0 && onRestore && (
                <button type="button" className="btn btn-secondary btn-md" onClick={() => onRestore(bouquet)}>
                  Восстановить
                </button>
              )
            )}
          </div>
        )}
      </div>
      {lightbox && (
        <PhotoLightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </Modal>
  );
}


function BouquetDisassembleModal({ bouquet, onClose, onDone, showToast }) {
  // Партии: истёкшие первыми, затем по сроку (ближайшая к протуханию выше).
  const lots = React.useMemo(() => {
    const arr = [...(bouquet.lots || [])];
    arr.sort((a, b2) => (Number(!!b2.is_expired) - Number(!!a.is_expired)) || ((a.expiry_date || '') < (b2.expiry_date || '') ? -1 : 1));
    return arr;
  }, [bouquet.lots]);
  const [selectedLotId, setSelectedLotId] = useState(lots.length ? lots[0].id : null);
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState('rework');
  const [saving, setSaving] = useState(false);
  const [extendDays, setExtendDays] = useState('');
  const [extending, setExtending] = useState(false);
  const selectedLot = lots.find(l => l.id === selectedLotId) || null;

  const handleExtend = async () => {
    const d = parseInt(extendDays);
    if (!d || d <= 0) { showToast('Укажите число дней > 0', 'error'); return; }
    setExtending(true);
    try {
      await api.updateBouquetLot(bouquet.id, selectedLotId, { shelf_life_days: d });
      showToast('Срок партии обновлён', 'success');
      onDone();
    } catch (e) {
      showToast(e.message || 'Ошибка', 'error');
    } finally {
      setExtending(false);
    }
  };

  // Разбираем ПО ОДНОМУ экземпляру — резерв компонентов считаем на 1 букет (состав рецепта).
  React.useEffect(() => {
    const lot = lots.find(l => l.id === selectedLotId);
    setItems(bouquet.components.map(c => ({
      component_id: c.id,
      product_name: c.product_name,
      reserved: c.quantity,         // на 1 экземпляр
      returned_quantity: c.quantity,
    })));
    // авто-причина «истекают» для истёкшей партии
    if (lot && lot.is_expired) setReason('expired');
  }, [selectedLotId]);

  const totalWaste = items.reduce((s, i) => s + (i.reserved - i.returned_quantity), 0);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.disassembleBouquet(bouquet.id, items.map(i => ({
        component_id: i.component_id,
        returned_quantity: i.returned_quantity,
      })), reason, selectedLotId);
      showToast('Партия разобрана', 'success');
      onDone();
    } catch (e) {
      showToast(e.message || 'Ошибка разборки', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} confirmOverlayClose title={`Разборка букета: ${bouquet.name}`}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: GRAY_TEXT }}>
          Разбираем <b>один экземпляр</b> из партии. Укажите, сколько стеблей по каждому компоненту
          вернётся на склад — разница спишется как порча. Чтобы разобрать несколько — повторите по очереди.
        </div>

        {lots.length > 0 && (
          <div>
            <Label>Партия для разборки</Label>
            <StyledSelect
              value={selectedLotId}
              onChange={(v) => setSelectedLotId(v)}
              options={lots.map(l => ({
                value: l.id,
                label: `Сборка ${new Date(l.assembled_at + 'T00:00:00').toLocaleDateString('ru-RU')} · ${l.quantity} шт · до ${new Date(l.expiry_date + 'T00:00:00').toLocaleDateString('ru-RU')}${l.created_by_name ? ' · ' + l.created_by_name : ''}${l.is_expired ? ' · ИСТЁК' : ''}`,
              }))}
            />
            {selectedLot && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, flexWrap: 'wrap' }}>
                <span style={{ color: selectedLot.is_expired ? CORAL : GRAY_TEXT }}>
                  Срок: до {new Date(selectedLot.expiry_date + 'T00:00:00').toLocaleDateString('ru-RU')}{selectedLot.is_expired ? ' (истёк)' : ''}{selectedLot.created_by_name ? ` · собрал: ${selectedLot.created_by_name}` : ''}
                </span>
                <input
                  type="text" inputMode="numeric" placeholder="новый срок, дн"
                  value={extendDays}
                  onChange={e => setExtendDays(e.target.value.replace(/\D/g, ''))}
                  style={{ width: 120, height: 32, boxSizing: 'border-box', padding: '0 8px', border: `1px solid ${BORDER}`, '--ch': '6px', '--stroke': (BORDER), fontSize: 13, textAlign: 'center' }}
                />
                <button
                  type="button" className="btn btn-secondary btn-sm" onClick={handleExtend} disabled={extending}
                >{extending ? '…' : 'Изменить срок'}</button>
              </div>
            )}
          </div>
        )}

        <div style={{ border: `1px solid ${BORDER}`, '--ch': '8px', '--stroke': (BORDER), padding: 12, display: 'flex', flexDirection: 'column' }}>
          {items.map((it, idx, arr) => {
            const waste = it.reserved - it.returned_quantity;
            const setQty = (v) => {
              const nv = Math.max(0, Math.min(it.reserved, v));
              setItems(prev => prev.map((p, i) => i === idx ? { ...p, returned_quantity: nv } : p));
            };
            return (
              <div key={it.component_id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
                borderBottom: idx < arr.length - 1 ? '1px solid #F4F4EF' : 'none',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: '#1C3D2E' }}>{it.product_name}</div>
                  <div style={{ fontSize: 12, color: GRAY_TEXT }}>
                    В букете было: {it.reserved} шт{waste > 0 && <span style={{ color: CORAL }}> · порча: {waste}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                  <span style={{ fontSize: 10, color: GRAY_TEXT, textTransform: 'uppercase', letterSpacing: '0.3px' }}>вернётся на склад</span>
                  <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${BORDER}`, '--ch': '6px', '--stroke': (BORDER), overflow: 'hidden' }}>
                    <button type="button" onClick={() => setQty(it.returned_quantity - 1)}
                      style={{ width: 34, height: 36, border: 'none', background: 'transparent', color: BRAND_GREEN, fontSize: 18, cursor: 'pointer', fontWeight: 600 }}>−</button>
                    <input
                      type="text" inputMode="numeric" value={it.returned_quantity}
                      onChange={e => setQty(parseInt(e.target.value.replace(/\D/g, '')) || 0)}
                      onFocus={e => e.target.select()}
                      style={{ width: 46, height: 36, padding: 0, border: 'none', borderLeft: `1px solid ${BORDER}`, borderRight: `1px solid ${BORDER}`, fontSize: 14, fontWeight: 600, textAlign: 'center', outline: 'none' }}
                    />
                    <button type="button" onClick={() => setQty(it.returned_quantity + 1)}
                      style={{ width: 34, height: 36, border: 'none', background: 'transparent', color: BRAND_GREEN, fontSize: 18, cursor: 'pointer', fontWeight: 600 }}>+</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <Label>Причина разборки</Label>
          <StyledSelect
            value={reason}
            onChange={(v) => setReason(v)}
            options={[
              { value: 'rework', label: 'Перевыкладка' },
              { value: 'damaged', label: 'Повреждены' },
              { value: 'expired', label: 'Истекают' },
            ]}
          />
        </div>

        {totalWaste > 0 && (
          <div style={{ padding: '10px 12px', background: '#F9EBCF', border: `1px solid #E5C97F`, '--ch': '6px', '--stroke': ('#E5C97F'), fontSize: 13, color: '#7E5D00' }}>
            ⚠️ Будет списано как порча: <b>{totalWaste} шт.</b>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Разбираем...' : 'Разобрать'}
          </button>
        </div>
      </div>
    </Modal>
  );
}


function BouquetReplenishModal({ bouquet, onClose, onDone, showToast }) {
  const [quantity, setQuantity] = useState(1);
  const [floristId, setFloristId] = useState('');
  const [florists, setFlorists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  // Срок жизни/дата сборки — для немедленной фиксации готовых (без флориста).
  const [shelfLife, setShelfLife] = useState('');  // auto если пусто
  // Дата сборки по умолчанию — сегодня по МСК (как считает сервер). toISOString()
  // ночью по МСК отдавал вчерашний день → авто-срок был на 1 больше.
  const [arrival, setArrival] = useState(todayMSKString);

  const reloadTasks = React.useCallback(() => {
    if ((bouquet.pending_assembly_qty || 0) > 0) {
      api.getBouquetAssemblyTasks(bouquet.id).then(setTasks).catch(() => {});
    }
  }, [bouquet.id, bouquet.pending_assembly_qty]);

  useEffect(() => {
    api.getEmployees('florist').then(setFlorists).catch(() => {});
    reloadTasks();
  }, [reloadTasks]);

  const phys = bouquet.physical_quantity || 0;

  const handleSubmit = async () => {
    if (quantity <= 0) { showToast('Количество должно быть > 0', 'error'); return; }
    setSaving(true);
    try {
      if (floristId) {
        // Админ задаёт срок и дату сборки заданию (можно будущую) — флорист их не меняет.
        await api.createAssemblyTask(
          bouquet.id,
          quantity,
          parseInt(floristId),
          shelfLife ? parseInt(shelfLife) : null,
          arrival || null,
        );
        showToast(`Задание флористу на ${quantity} шт создано`, 'success');
      } else {
        await api.assembleBouquet(
          bouquet.id,
          quantity,
          shelfLife ? parseInt(shelfLife) : null,
          arrival || null,
        );
        showToast(`Добавлено ${quantity} готовых`, 'success');
      }
      onDone();
    } catch (e) {
      showToast(e.message || 'Ошибка', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelTask = async (taskId) => {
    try {
      await api.cancelAssemblyTask(taskId);
      showToast('Задание отменено, цветы возвращены на склад', 'success');
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (e) {
      showToast(e.message || 'Ошибка отмены', 'error');
    }
  };

  // Живой расчёт «годен до» = дата сборки + срок (дн.). Пусто при авто-сроке.
  const bestBeforeStr = (() => {
    if (!shelfLife || !arrival) return '';
    const d = new Date(arrival + 'T00:00:00');
    d.setDate(d.getDate() + parseInt(shelfLife));
    return d.toLocaleDateString('ru-RU');
  })();
  // Авто-срок (зеркало бэка): min остатка жизни компонентов на дату сборки;
  // если у всех остаток ≤ 0 (даты прошли) — min полного срока компонентов.
  const autoShelfDays = (() => {
    const comps = bouquet.components || [];
    if (!arrival) return null;
    const arr = new Date(arrival + 'T00:00:00');
    const remaining = [];
    const fullShelves = [];
    comps.forEach(c => {
      if (c.product_shelf_life_days) fullShelves.push(c.product_shelf_life_days);
      if (c.product_expiry_date) {
        const rem = Math.round((new Date(c.product_expiry_date + 'T00:00:00') - arr) / 86400000);
        if (rem > 0) remaining.push(rem);
      }
    });
    if (remaining.length) return Math.min(...remaining);
    if (fullShelves.length) return Math.min(...fullShelves);
    return null;
  })();
  const autoBestBeforeStr = (() => {
    if (autoShelfDays == null || !arrival) return null;
    const d = new Date(arrival + 'T00:00:00');
    d.setDate(d.getDate() + autoShelfDays);
    return d.toLocaleDateString('ru-RU');
  })();
  const SHELF_PRESETS = [
    { v: '', l: 'Авто' }, { v: '3', l: '3 дн' }, { v: '5', l: '5 дн' }, { v: '7', l: '7 дн' },
  ];
  // Последняя партия (с самой поздней датой истечения) — для подсказки в составе.
  const lastLot = (bouquet.lots || []).length
    ? (bouquet.lots).reduce((a, b2) => ((a.expiry_date || '') >= (b2.expiry_date || '') ? a : b2))
    : null;
  // Доступно к сборке на выбранную дату — из готового механизма daily_availability (capacity по датам).
  const _availRow = (bouquet.daily_availability || []).find(d => d.date === arrival);
  const availOnDate = _availRow ? (_availRow.capacity || 0) : null;  // null = дата вне окна/нет данных
  const arrivalDM = arrival ? arrival.split('-').reverse().slice(0, 2).join('.') : '';

  return (
    <Modal isOpen={true} onClose={onClose} confirmOverlayClose title={`Пополнить: ${bouquet.name}`}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ border: `1px solid ${BORDER}`, '--ch': '8px', '--stroke': (BORDER), padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: GRAY_TEXT, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Состав одного букета
            </span>
            <span style={{ fontSize: 12, color: BRAND_GREEN, fontWeight: 600 }}>
              Собрано: {phys}{tasks.length > 0 && ` · в работе: ${tasks.length}`}
              {lastLot && ` · партия до ${new Date(lastLot.expiry_date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {(bouquet.components || []).map((c, i, arr) => {
              // Из ИСТЁКШИХ стеблей собирать нельзя → к сборке 0 (хотя на складе число есть).
              const expired = !!c.product_is_expired;
              const rawAvail = c.product_available;
              const avail = expired ? 0 : rawAvail;
              const enough = rawAvail == null || avail >= (c.quantity || 0);
              const canMake = rawAvail != null ? Math.floor(avail / (c.quantity || 1)) : null;
              return (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 14, padding: '7px 0',
                  borderBottom: i < arr.length - 1 ? '1px solid #F4F4EF' : 'none',
                }}>
                  <span style={{ color: '#1C3D2E' }}>
                    {c.product_name}
                    {rawAvail != null && (
                      <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 600, color: enough ? '#4A8C6E' : CORAL }}>
                        {expired
                          ? `· истёк срок — к сборке 0 (на складе ${rawAvail})`
                          : `· на складе ${avail}${canMake != null ? ` (на ${canMake} шт)` : ''}`}
                      </span>
                    )}
                  </span>
                  <span style={{ color: GRAY_TEXT, fontWeight: 600 }}>× {c.quantity} шт</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, alignItems: 'start' }}>
          <div>
            <Label>Количество</Label>
            <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${BORDER}`, '--ch': '6px', '--stroke': (BORDER), overflow: 'hidden', width: 'fit-content' }}>
              <button type="button" onClick={() => setQuantity(q => Math.max(1, (parseInt(q) || 1) - 1))}
                style={{ width: 40, height: 40, border: 'none', background: 'transparent', color: BRAND_GREEN, fontSize: 20, cursor: 'pointer', fontWeight: 600 }}>−</button>
              <input
                type="text" inputMode="numeric" value={quantity}
                onChange={e => { const v = e.target.value.replace(/\D/g, ''); setQuantity(v === '' ? '' : parseInt(v)); }}
                onBlur={() => setQuantity(q => Math.max(1, parseInt(q) || 1))}
                onFocus={e => e.target.select()}
                style={{ width: 56, height: 40, padding: 0, border: 'none', borderLeft: `1px solid ${BORDER}`, borderRight: `1px solid ${BORDER}`, fontSize: 15, fontWeight: 600, textAlign: 'center', outline: 'none' }}
              />
              <button type="button" onClick={() => setQuantity(q => (parseInt(q) || 0) + 1)}
                style={{ width: 40, height: 40, border: 'none', background: 'transparent', color: BRAND_GREEN, fontSize: 20, cursor: 'pointer', fontWeight: 600 }}>+</button>
            </div>
          </div>
          <div>
            <Label>Флорист</Label>
            <StyledSelect
              value={floristId}
              onChange={(v) => setFloristId(v)}
              placeholder="Уже собрано — добавить готовыми"
              options={[
                { value: '', label: 'Уже собрано — добавить готовыми' },
                ...florists.map(f => ({ value: f.id, label: `Задание: ${f.name}` })),
              ]}
            />
          </div>
        </div>

        {(
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 12, alignItems: 'start' }}>
              <div>
                <Label>Дата сборки</Label>
                <Input type="date" value={arrival} onChange={e => setArrival(e.target.value)} />
              </div>
              <div>
                <Label>Срок жизни букета</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  {/* Авто / 3 / 5 / 7 дн — переключатель «одно из» (UX-аудит 25.10) */}
                  <div className="seg">
                    {SHELF_PRESETS.map(opt => (
                      <button key={opt.l} type="button" className={`seg-btn${String(shelfLife) === opt.v ? ' on' : ''}`} onClick={() => setShelfLife(opt.v)}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text" inputMode="numeric" placeholder="свой"
                    value={SHELF_PRESETS.some(p => p.v === String(shelfLife)) ? '' : shelfLife}
                    onChange={e => setShelfLife(e.target.value.replace(/\D/g, ''))}
                    style={{ width: 64, height: 40, boxSizing: 'border-box', padding: '0 8px', border: `1px solid ${BORDER}`, '--ch': '6px', '--stroke': (BORDER), fontSize: 13, textAlign: 'center' }}
                  />
                </div>
              </div>
            </div>

            {/* Доступно к сборке на выбранную дату (механизм capacity по датам) */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', '--ch': '6px',
              background: (availOnDate == null || availOnDate > 0) ? '#EAF5EC' : '#FFF4F0',
            }}>
              <span style={{ fontSize: 13, color: GRAY_TEXT }}>
                Доступно к сборке{arrivalDM ? ` на ${arrivalDM}` : ''}:
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, color: availOnDate > 0 ? '#1F763B' : (availOnDate === 0 ? CORAL : GRAY_TEXT) }}>
                {availOnDate == null ? 'нет данных на дату' : (availOnDate > 0 ? `${availOnDate} шт` : 'нет в наличии')}
              </span>
            </div>
            {availOnDate != null && parseInt(quantity) > availOnDate && (
              <div style={{ fontSize: 12, color: CORAL }}>
                Запрошено {quantity} — больше, чем доступно ({availOnDate}). Операция может отклониться.
              </div>
            )}

            <div style={{ padding: '10px 12px', background: '#EFFAF4', '--ch': '6px', fontSize: 13, color: BRAND_GREEN, fontWeight: 600 }}>
              {shelfLife
                ? `Годен до: ${bestBeforeStr} (${shelfLife} дн. от даты сборки)`
                : (autoBestBeforeStr
                    ? `Годен до: ${autoBestBeforeStr} (авто ${autoShelfDays} дн. — по минимуму компонентов)`
                    : 'Срок рассчитается автоматически по минимуму остатка жизни компонентов')}
            </div>
          </>
        )}


        {tasks.length > 0 && (
          <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 10 }}>
            <Label>Активные задания на сборку (отмена возвращает цветы)</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {tasks.map(tk => (
                <div key={tk.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, padding: '4px 8px', background: '#F9F9F6', '--ch': '5px'}}>
                  <span>#{tk.id} · {tk.florist_name || 'без флориста'}</span>
                  {/* Отмена задания — красный контур, не терракота (UX-аудит 25.10) */}
                  <button type="button" className="btn btn-danger-outline btn-sm" onClick={() => handleCancelTask(tk.id)}>Отменить задание</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? '...' : (floristId ? 'Создать задание' : 'Добавить готовые')}
          </button>
        </div>
      </div>
    </Modal>
  );
}


// Кнопки — общие классы .btn-* / .seg / .tabs из styles.css (UX-аудит 25.10)

function Label({ children }) {
  return <div style={{ fontSize: 12, color: GRAY_TEXT, marginBottom: 4, fontWeight: 600 }}>{children}</div>;
}

function Input({ ...props }) {
  return <input {...props} style={{
    width: '100%', padding: 8, border: `1px solid ${BORDER}`, '--ch': '5px', '--stroke': (BORDER),
    fontSize: 14, boxSizing: 'border-box', ...(props.style || {}),
  }} />;
}
